/**
 * Utilidades compartidas SOLO entre las páginas bespoke de `finanzas-cxp` (Gastos,
 * GastosContenedor, Pagos, AnticiposProveedores). No se toca ningún archivo de
 * `components/common` — estos módulos necesitan tablas con acciones por fila
 * (Cancelar, Aplicar Anticipo, líneas de pago seleccionables) que el `ResourcePage`
 * genérico no soporta, así que replican aquí el patrón (fetch + tabla + paginación
 * cliente) en vez de forzarlo al componente compartido.
 *
 * Los endpoints de este módulo (`/gastos`, `/gastos-contenedor`, `/pagos`,
 * `/anticipos-proveedores`) NO son paginados en el servidor (services custom que
 * regresan el arreglo completo de activos, ver `finanzas-gastos/*.service.js`) — por
 * eso la búsqueda/orden/paginación de estas páginas es 100% en cliente (mismo
 * criterio que V1), a diferencia de `useListado` (que asume paginación real del
 * servidor, usado por los catálogos simples de `resources.js`).
 */
import { useState, useMemo } from 'react';

export function formatMoney(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(Number(n || 0));
}

export function formatDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = String(iso).split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

export function today() {
  return new Date().toISOString().slice(0, 10);
}

export function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

/** ref de mongoose puede venir como arreglo `[doc]` o como doc directo — normaliza a un solo doc/id. */
export function primero(valor) {
  return Array.isArray(valor) ? valor[0] : valor;
}

/**
 * Paginación + búsqueda + orden 100% en cliente sobre un arreglo ya cargado —
 * mismo rol que `useListado`, pero sin depender de `meta.pagination` del backend
 * (estos 4 endpoints no lo devuelven).
 */
export function useLocalTable(rows, { searchFields = [], perPage = 20, defaultSortBy = null, defaultSortOrder = 'desc' } = {}) {
  const [search, setSearchRaw] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState(defaultSortBy);
  const [sortOrder, setSortOrder] = useState(defaultSortOrder);

  const setSearch = (v) => { setSearchRaw(v); setPage(1); };

  const filtradas = useMemo(() => {
    let r = rows || [];
    if (search) {
      const s = search.toLowerCase();
      r = r.filter((row) => searchFields.some((f) => String(row[f] ?? '').toLowerCase().includes(s)));
    }
    if (sortBy) {
      r = [...r].sort((a, b) => {
        const av = a[sortBy]; const bv = b[sortBy];
        let cmp;
        if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
        else cmp = String(av ?? '').localeCompare(String(bv ?? ''));
        return sortOrder === 'asc' ? cmp : -cmp;
      });
    }
    return r;
  }, [rows, search, sortBy, sortOrder]);

  const totalDocs = filtradas.length;
  const totalPages = Math.max(1, Math.ceil(totalDocs / perPage));
  const pageRows = filtradas.slice((page - 1) * perPage, page * perPage);

  function ordenarPor(field) {
    setPage(1);
    if (field === sortBy) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(field); setSortOrder('asc'); }
  }

  // `filtradas`: el listado completo YA filtrado/ordenado pero SIN paginar -- se
  // agrega para exportación PDF/Excel (`utils/exportUtils.js`), que debe exportar el
  // listado filtrado completo, igual que V1, no solo la página visible (`pageRows`).
  // Aditivo: no cambia el shape que ya consumían Gastos/GastosContenedor/Pagos/
  // AnticiposProveedores.
  return { search, setSearch, page, setPage, sortBy, sortOrder, ordenarPor, pageRows, filtradas, pagination: { totalDocs, totalPages } };
}

export default { formatMoney, formatDate, today, daysAgo, primero, useLocalTable };
